import { PropsWithChildren } from 'react';
import {
  Pressable,
  PressableProps,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import { Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';

export type ButtonVariant =
  | 'default'
  | 'gold'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'link';

export type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

export type ButtonProps = PropsWithChildren<
  Omit<PressableProps, 'style' | 'children'> & {
    variant?: ButtonVariant;
    size?: ButtonSize;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    fullWidth?: boolean;
  }
>;

export function Button({
  variant = 'default',
  size = 'default',
  style,
  textStyle,
  children,
  leadingIcon,
  trailingIcon,
  fullWidth,
  disabled,
  ...rest
}: ButtonProps) {
  const theme = useTheme();

  const containerBase: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.two,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: 'transparent',
    alignSelf: fullWidth ? 'stretch' : 'flex-start',
  };

  const sizeStyle: ViewStyle =
    size === 'sm'
      ? { paddingVertical: Spacing.one + 2, paddingHorizontal: Spacing.three, minHeight: 32 }
      : size === 'lg'
        ? { paddingVertical: Spacing.two + 2, paddingHorizontal: Spacing.four, minHeight: 44 }
        : size === 'icon'
          ? { width: 40, height: 40, paddingHorizontal: 0 }
          : { paddingVertical: Spacing.two + 2, paddingHorizontal: Spacing.three, minHeight: 40 };

  let containerVariant: ViewStyle = {};
  let color: string = theme.primaryForeground;
  switch (variant) {
    case 'gold':
      containerVariant = { backgroundColor: theme.gold };
      color = theme.goldForeground;
      break;
    case 'outline':
      containerVariant = { backgroundColor: 'transparent', borderColor: theme.border };
      color = theme.text;
      break;
    case 'secondary':
      containerVariant = { backgroundColor: theme.secondary };
      color = theme.secondaryForeground;
      break;
    case 'ghost':
      containerVariant = { backgroundColor: 'transparent' };
      color = theme.text;
      break;
    case 'destructive':
      containerVariant = { backgroundColor: theme.destructive };
      color = '#fff';
      break;
    case 'link':
      containerVariant = { backgroundColor: 'transparent' };
      color = theme.primary;
      break;
    case 'default':
    default:
      containerVariant = { backgroundColor: theme.primary };
      color = theme.primaryForeground;
      break;
  }

  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled}
      {...rest}
      style={({ pressed }) => [
        containerBase,
        sizeStyle,
        containerVariant,
        disabled && styles.disabled,
        pressed && styles.pressed,
        style,
      ]}>
      {leadingIcon ? <View>{leadingIcon}</View> : null}
      {typeof children === 'string' ? (
        <Text style={[styles.text, { color }, textStyle]}>{children}</Text>
      ) : (
        children
      )}
      {trailingIcon ? <View>{trailingIcon}</View> : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  text: {
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.1,
  },
  pressed: {
    opacity: 0.85,
  },
  disabled: {
    opacity: 0.5,
  },
});
